import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary } from './CommissionOverview';
import { FormRow, overlayStyle, modalStyle } from './CommissionBeneficiaries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const SCOPE_TYPES = ['company', 'department', 'team', 'employee', 'external_user', 'lead_source', 'client', 'product_category', 'product', 'service_category', 'service', 'sales_type', 'specific_deal', 'specific_contract', 'specific_invoice', 'combination'];
const CALC_TYPES = ['percentage', 'fixed', 'tiered_percentage', 'tiered_fixed', 'recurring_percentage', 'recurring_fixed', 'declining_recurring', 'changing_recurring', 'custom_combination'];
const CALC_BASIS = ['gross_sale', 'net_sale', 'after_discount', 'amount_collected', 'profit', 'margin', 'product_value', 'service_value', 'custom'];
const EARNING_TRIGGERS = ['deal_won', 'contract_confirmed', 'invoice_issued', 'customer_payment', 'full_settlement', 'custom'];
const CAP_TYPES = ['no_cap', 'per_transaction', 'per_month', 'per_quarter', 'per_year', 'per_agreement', 'per_beneficiary'];
const PRIORITY_LEVELS = ['general', 'department_team', 'beneficiary', 'lead_source', 'category', 'product_service', 'client', 'specific_deal'];
const STATUSES = ['draft', 'active', 'inactive', 'archived'];

export default function CommissionRules() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [simRule, setSimRule] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/commissions/rules?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/commissions/profiles?per_page=200`, { headers: getAuthHeaders() })
      .then(res => res.ok ? res.json() : { data: [] })
      .then(json => setProfiles(Array.isArray(json.data) ? json.data : []))
      .catch(() => setProfiles([]));
  }, [companyId]);

  const changeStatus = async (id, newStatus) => {
    try {
      await fetch(`${API}/companies/${companyId}/commissions/rules/${id}/status`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchList();
    } catch (e) { console.error(e); }
  };

  const removeDraft = async (id) => {
    if (!window.confirm('Delete this draft rule?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/rules/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed to delete'); return; }
      fetchList();
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📐 Commission Rules</h2>
        <button style={btnPrimary} onClick={() => setEditing('new')}>+ New Rule</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 220 }} />
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 160 }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Scope', 'Priority', 'Stack/Replace', 'Status', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No rules yet</td></tr>}
              {list.map(r => (
                <tr key={r.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{r.rule_code} <span style={{ color: '#9ca3af' }}>v{r.version_number}</span></td>
                  <td style={tdStyle}>{r.name}</td>
                  <td style={tdStyle}>{(r.scope_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{(r.priority_level || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{r.stack_or_replace}</td>
                  <td style={tdStyle}><Badge value={r.status} /></td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={btnSecondary} onClick={() => setEditing(r)}>Edit</button>
                      <button style={btnSecondary} onClick={() => setSimRule(r)}>Simulate</button>
                      {r.status === 'draft' && <button style={btnSecondary} onClick={() => changeStatus(r.id, 'active')}>Activate</button>}
                      {r.status === 'active' && <button style={btnSecondary} onClick={() => changeStatus(r.id, 'inactive')}>Deactivate</button>}
                      {r.status === 'draft' && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => removeDraft(r.id)}>Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <RuleModal companyId={companyId} rule={editing === 'new' ? null : editing} profiles={profiles}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />
      )}
      {simRule && <SimulateModal companyId={companyId} rule={simRule} onClose={() => setSimRule(null)} />}
    </div>
  );
}

function SimulateModal({ companyId, rule, onClose }) {
  const [base, setBase] = useState('1000');
  const [result, setResult] = useState(null);
  const [overlaps, setOverlaps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/commissions/rules/${rule.id}/overlap-check`, { headers: getAuthHeaders() })
      .then(res => res.ok ? res.json() : { potential_conflicts: [] })
      .then(json => setOverlaps(Array.isArray(json.potential_conflicts) ? json.potential_conflicts : []))
      .catch(() => setOverlaps([]));
  }, [companyId, rule.id]);

  const run = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/rules/${rule.id}/simulate`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ base_amount: base }),
      });
      if (!res.ok) throw new Error('Failed');
      setResult(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Simulate: {rule.name}</h3>
        <FormRow label="Base Amount">
          <input type="number" value={base} onChange={e => setBase(e.target.value)} style={inputStyle} />
        </FormRow>
        <button style={btnPrimary} onClick={run} disabled={loading}>{loading ? 'Calculating...' : 'Run Simulation'}</button>
        {result && (
          <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
            <div><strong>Commission Amount:</strong> ${Number(result.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <div><strong>Calculation Type:</strong> {result.calculation_type}</div>
            <div><strong>Rate Applied:</strong> {result.rate_applied ?? '—'}</div>
            {Array.isArray(result.contributions) && result.contributions.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong>Breakdown:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {result.contributions.map((c, i) => <li key={i}>{JSON.stringify(c)}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        {overlaps.length > 0 && (
          <div style={{ marginTop: 14, padding: 12, background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
            <strong style={{ color: '#b45309' }}>⚠️ Potential Conflicts ({overlaps.length})</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {overlaps.map(o => <li key={o.id}>{o.name} ({o.rule_code})</li>)}
            </ul>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function RuleModal({ companyId, rule, profiles, onClose, onSaved }) {
  const [form, setForm] = useState(rule ? { ...rule } : {
    name: '', profile_id: '', scope_type: 'company', beneficiary_scope: 'all',
    calculation_type: 'percentage', calculation_basis: 'after_discount', tax_treatment: 'after_tax',
    rate: '', fixed_amount: '', tier_method: 'progressive', earning_trigger: 'customer_payment',
    holding_period_days: 0, min_commission: '', max_commission: '', cap_type: 'no_cap', cap_amount: '',
    priority_level: 'general', priority_order: 0, stack_or_replace: 'replace', applicable: true,
  });
  const [tierRows, setTierRows] = useState(rule?.tier_config && Array.isArray(rule.tier_config) ? rule.tier_config : [{ from: 0, to: '', rate: '', amount: '' }]);
  const [createNewVersion, setCreateNewVersion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const u = (field, val) => setForm({ ...form, [field]: val });
  const isTiered = (form.calculation_type || '').includes('tiered');
  const isFixed = (form.calculation_type || '') === 'fixed' || (form.calculation_type || '') === 'recurring_fixed';

  const submit = async () => {
    if (!form.name) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form };
      if (isTiered) payload.tier_config = tierRows;
      if (rule) payload.create_new_version = createNewVersion;
      const url = rule ? `${API}/companies/${companyId}/commissions/rules/${rule.id}` : `${API}/companies/${companyId}/commissions/rules`;
      const res = await fetch(url, {
        method: rule ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || 'Failed to save'); }
      onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 660 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{rule ? 'Edit Rule' : 'New Commission Rule'}</h3>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Name *"><input value={form.name || ''} onChange={e => u('name', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Linked Profile">
            <select value={form.profile_id || ''} onChange={e => u('profile_id', e.target.value)} style={inputStyle}>
              <option value="">None</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormRow>
          <FormRow label="Scope Type">
            <select value={form.scope_type || 'company'} onChange={e => u('scope_type', e.target.value)} style={inputStyle}>
              {SCOPE_TYPES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Beneficiary Scope">
            <select value={form.beneficiary_scope || 'all'} onChange={e => u('beneficiary_scope', e.target.value)} style={inputStyle}>
              <option value="all">All</option><option value="specific">Specific</option>
            </select>
          </FormRow>
          <FormRow label="Calculation Type">
            <select value={form.calculation_type || 'percentage'} onChange={e => u('calculation_type', e.target.value)} style={inputStyle}>
              {CALC_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Calculation Basis">
            <select value={form.calculation_basis || 'after_discount'} onChange={e => u('calculation_basis', e.target.value)} style={inputStyle}>
              {CALC_BASIS.map(b => <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          {!isTiered && (isFixed ? (
            <FormRow label="Fixed Amount"><input type="number" value={form.fixed_amount || ''} onChange={e => u('fixed_amount', e.target.value)} style={inputStyle} /></FormRow>
          ) : (
            <FormRow label="Rate (%)"><input type="number" value={form.rate || ''} onChange={e => u('rate', e.target.value)} style={inputStyle} /></FormRow>
          ))}
          {isTiered && (
            <FormRow label="Tier Method">
              <select value={form.tier_method || 'progressive'} onChange={e => u('tier_method', e.target.value)} style={inputStyle}>
                <option value="progressive">Progressive</option><option value="full_amount">Full Amount</option>
              </select>
            </FormRow>
          )}
          <FormRow label="Earning Trigger">
            <select value={form.earning_trigger || 'customer_payment'} onChange={e => u('earning_trigger', e.target.value)} style={inputStyle}>
              {EARNING_TRIGGERS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Holding Period (days)"><input type="number" value={form.holding_period_days || 0} onChange={e => u('holding_period_days', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Priority Level">
            <select value={form.priority_level || 'general'} onChange={e => u('priority_level', e.target.value)} style={inputStyle}>
              {PRIORITY_LEVELS.map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Priority Order"><input type="number" value={form.priority_order || 0} onChange={e => u('priority_order', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Stack or Replace">
            <select value={form.stack_or_replace || 'replace'} onChange={e => u('stack_or_replace', e.target.value)} style={inputStyle}>
              <option value="stack">Stack</option><option value="replace">Replace</option>
            </select>
          </FormRow>
          <FormRow label="Min Commission"><input type="number" value={form.min_commission || ''} onChange={e => u('min_commission', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Max Commission"><input type="number" value={form.max_commission || ''} onChange={e => u('max_commission', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Cap Type">
            <select value={form.cap_type || 'no_cap'} onChange={e => u('cap_type', e.target.value)} style={inputStyle}>
              {CAP_TYPES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          {form.cap_type !== 'no_cap' && (
            <FormRow label="Cap Amount"><input type="number" value={form.cap_amount || ''} onChange={e => u('cap_amount', e.target.value)} style={inputStyle} /></FormRow>
          )}
          <FormRow label="Effective From"><input type="date" value={(form.effective_from || '').split('T')[0] || ''} onChange={e => u('effective_from', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Effective To"><input type="date" value={(form.effective_to || '').split('T')[0] || ''} onChange={e => u('effective_to', e.target.value)} style={inputStyle} /></FormRow>
        </div>

        {isTiered && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Tiers (From / To / {form.calculation_type === 'tiered_fixed' ? 'Amount' : 'Rate %'})</div>
            {tierRows.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input type="number" placeholder="From" value={t.from} onChange={e => { const r = [...tierRows]; r[i] = { ...r[i], from: e.target.value }; setTierRows(r); }} style={inputStyle} />
                <input type="number" placeholder="To (blank = ∞)" value={t.to} onChange={e => { const r = [...tierRows]; r[i] = { ...r[i], to: e.target.value }; setTierRows(r); }} style={inputStyle} />
                {form.calculation_type === 'tiered_fixed' ? (
                  <input type="number" placeholder="Amount" value={t.amount} onChange={e => { const r = [...tierRows]; r[i] = { ...r[i], amount: e.target.value }; setTierRows(r); }} style={inputStyle} />
                ) : (
                  <input type="number" placeholder="Rate %" value={t.rate} onChange={e => { const r = [...tierRows]; r[i] = { ...r[i], rate: e.target.value }; setTierRows(r); }} style={inputStyle} />
                )}
                <button style={btnSecondary} onClick={() => setTierRows(tierRows.filter((_, idx) => idx !== i))}>✕</button>
              </div>
            ))}
            <button style={btnSecondary} onClick={() => setTierRows([...tierRows, { from: '', to: '', rate: '', amount: '' }])}>+ Add Tier</button>
          </div>
        )}

        <FormRow label="Applicable" >
          <input type="checkbox" checked={!!form.applicable} onChange={e => u('applicable', e.target.checked)} />
        </FormRow>

        {rule && rule.status === 'active' && (
          <FormRow label="Create New Version (instead of editing active rule)">
            <input type="checkbox" checked={createNewVersion} onChange={e => setCreateNewVersion(e.target.checked)} />
          </FormRow>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
